/**
 * CustomerMatchRenderer — Sprint 2 minimal renderer for displayType='customer_match'.
 *
 * Per v1.1 amendments Q6: customer_match ships in Sprint 2 because
 * customer-misidentification is the highest-likelihood failure in the
 * reference workflow (Domain CRIT-2). Without a dedicated renderer the
 * disambiguation step looks like a JSON blob.
 *
 * Sprint 2 scope is intentionally minimal — polished UI ships with the
 * Sprint 12 hand-designed renderer batch. We deliver:
 *   1. Incoming record summary at the top (read-only)
 *   2. Candidate list with score + "Match" button per row
 *   3. "Create new customer" affordance via the standard ActionBar
 *
 * Schema contract:
 *   - evidence.incoming         — the record we're trying to match
 *   - evidence.candidates       — array of { id, name, score, ...details }
 *   - actions                   — at least one reject/create-new path
 *
 * The "Match" buttons dispatch through `onDecide` with `action.decision =
 * 'custom'` and the chosen candidate id written to
 * `evidence.selected_customer_id` so a workflow author can route the
 * outcome via the same evidence edits used elsewhere.
 */

import * as React from 'react';
import { Users, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ActionBar } from '../ActionBar';
import { cn } from '@/lib/utils';
import { readPath } from '@argusai/shared';
import type { Action } from '@argusai/shared';
import type { RendererComponent } from '../AutoRenderedApprovalCard';

interface Candidate {
  id: string;
  name?: string;
  score?: number;
  [key: string]: unknown;
}

function toCandidates(raw: unknown): Candidate[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
    .map((c) => ({
      id: String(c.id ?? c.customerId ?? ''),
      name: typeof c.name === 'string' ? c.name : undefined,
      score: typeof c.score === 'number' ? c.score : undefined,
      ...c,
    }))
    .filter((c) => c.id !== '');
}

function ScoreBadge({ score }: { score?: number }) {
  if (typeof score !== 'number') return null;
  const pct = Math.round(score * 100);
  let variant: 'default' | 'secondary' | 'outline' = 'outline';
  if (score >= 0.9) variant = 'default';
  else if (score >= 0.6) variant = 'secondary';
  return (
    <Badge variant={variant} className="text-[10px] tabular-nums">
      {pct}% match
    </Badge>
  );
}

function IncomingRecord({ incoming }: { incoming: Record<string, unknown> }) {
  const entries = Object.entries(incoming).filter(
    ([k]) => k !== 'candidates' && k !== 'selected_customer_id',
  );
  if (entries.length === 0) {
    return (
      <p className="text-xs italic text-muted-foreground">
        No incoming record data available
      </p>
    );
  }
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
      {entries.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="break-words font-medium">
            {typeof v === 'string' || typeof v === 'number'
              ? String(v)
              : JSON.stringify(v)}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export const CustomerMatchRenderer: RendererComponent = ({
  approval,
  schema,
  evidence,
  onFieldChange,
  onDecide,
  disabled,
}) => {
  // Incoming record — workflow author may either nest it under
  // `evidence.incoming` or put fields directly on evidence. Prefer
  // `.incoming` if present.
  const incomingRaw = readPath(evidence, 'incoming') ?? evidence;
  const incoming =
    incomingRaw && typeof incomingRaw === 'object' && !Array.isArray(incomingRaw)
      ? (incomingRaw as Record<string, unknown>)
      : {};

  const candidates = toCandidates(readPath(evidence, 'candidates'));
  const selectedId = readPath(evidence, 'selected_customer_id') as
    | string
    | undefined;

  // Pre-fabricate the synthetic "match this candidate" action so the
  // existing ActionBar keyboard/confirmation contract still applies. We
  // dispatch the user's action click through onDecide so the parent can
  // run its mutation just like for an approve/reject.
  const matchAction = React.useCallback(
    (candidate: Candidate): Action => ({
      id: `match:${candidate.id}`,
      label: candidate.name
        ? `Match "${candidate.name}"`
        : `Match ${candidate.id}`,
      variant: 'primary',
      decision: 'custom',
    }),
    [],
  );

  const handleMatch = async (candidate: Candidate) => {
    // Write the selection into the working evidence then fire the
    // synthetic action. The parent decides whether to treat 'custom' as
    // an approve-with-overrides or as a separate branch.
    onFieldChange('selected_customer_id', candidate.id);
    await onDecide(matchAction(candidate));
  };

  return (
    <div className="grid gap-6 p-4 lg:grid-cols-[2fr,1fr]">
      <div className="space-y-4" data-renderer="customer_match">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Users className="h-4 w-4 text-primary" />
          <span>Customer match</span>
        </div>

        {/* Incoming record */}
        <section className="rounded-md border border-border/60 bg-muted/30 p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Incoming record
          </h3>
          <IncomingRecord incoming={incoming} />
        </section>

        {/* Candidates */}
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Candidate matches ({candidates.length})
          </h3>
          {candidates.length === 0 ? (
            <p className="text-xs italic text-muted-foreground">
              No candidate matches returned. Use the actions below to either
              create a new customer or reject this approval.
            </p>
          ) : (
            <ul className="space-y-2" data-candidate-list="true">
              {candidates.map((c) => {
                const isSelected = selectedId === c.id;
                return (
                  <li
                    key={c.id}
                    className={cn(
                      'flex items-start justify-between gap-3 rounded-md border bg-card p-3',
                      isSelected
                        ? 'border-primary ring-1 ring-primary/40'
                        : 'border-border/60',
                    )}
                    data-candidate-id={c.id}
                  >
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium">
                          {c.name ?? c.id}
                        </span>
                        <ScoreBadge score={c.score} />
                        {isSelected && (
                          <Badge
                            variant="default"
                            className="text-[10px]"
                            title="Selected"
                          >
                            <Check className="mr-0.5 h-2.5 w-2.5" />
                            Selected
                          </Badge>
                        )}
                      </div>
                      {/* Surface up to a couple of extra hints. */}
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                        {Object.entries(c)
                          .filter(
                            ([k]) =>
                              k !== 'id' && k !== 'name' && k !== 'score',
                          )
                          .slice(0, 3)
                          .map(([k, v]) => (
                            <span key={k}>
                              <span className="font-medium">{k}:</span>{' '}
                              {typeof v === 'string' || typeof v === 'number'
                                ? String(v)
                                : JSON.stringify(v)}
                            </span>
                          ))}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant={isSelected ? 'default' : 'outline'}
                      size="sm"
                      disabled={disabled}
                      onClick={() => {
                        void handleMatch(c);
                      }}
                      data-match-candidate-id={c.id}
                    >
                      {isSelected ? 'Re-match' : 'Match'}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Schema-declared actions — typically a "Create new customer"
            primary button + a "Reject" path. */}
        <ActionBar
          actions={schema.actions}
          approvalId={approval.id}
          onDecide={onDecide}
          disabled={disabled}
          className="rounded-md"
        />
      </div>

      {/* Right rail — context panels (e.g. previous orders for this email). */}
      <aside className="space-y-3">
        {schema.contextPanels.length === 0 && (
          <p className="text-xs italic text-muted-foreground">
            No context panels declared
          </p>
        )}
        {schema.contextPanels.map((panel) => (
          <section
            key={panel.panelId}
            className="rounded-md border border-border/60 bg-card p-3"
          >
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {panel.title}
            </h4>
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px]">
              {JSON.stringify(
                panel.dataPath
                  ? readPath(evidence, panel.dataPath)
                  : evidence,
                null,
                2,
              )}
            </pre>
          </section>
        ))}
      </aside>
    </div>
  );
};
